import { EyeIcon, EyeOffIcon } from 'lucide-react'
import React, { useState } from 'react'

import { renderFormattedSSN } from 'components/members/utils'
import { IconButton } from 'slab'
import { capitalize, cn, getFormattedDate, snakeToSentenceCase } from 'utils'
import { type MemberOnboardingMember } from 'utils/web_api.schemas'

import RenewalsMemberInfoEditForm, {
  LABEL_CONTAINER_CLASSES,
} from '../RenewalsMemberInfoEditForm/RenewalsMemberInfoEditForm'

interface RenewalsMemberInfoProps {
  member: MemberOnboardingMember;
  handleEditOpen: () => void;
  handleEditSuccessful: () => void;
  handleEditCancel: () => void;
  isMissingHouseholdIncome: boolean;
}

const RenewalsMemberInfo: React.FC<RenewalsMemberInfoProps> = ({
  member,
  isMissingHouseholdIncome,
  handleEditOpen,
  handleEditSuccessful,
  handleEditCancel,
}) => {
  const [showFullSSN, setShowFullSSN] = useState(false)

  const toggleSSNVisibility = () => {
    setShowFullSSN(!showFullSSN)
  }

  const occupation = snakeToSentenceCase(member.occupation ?? '')

  return (
    <div className='border border-gray-200 shadow-sm rounded-lg'>
      <div className='p-4 rounded-t-lg border-b border-gray-200 bg-thatch-50'>
        <div className='flex justify-between items-center gap-2 mb-4'>
          <div className='flex items-center gap-2'>
            <h3 className='body-bold sensitive'>
              {member.given_name} {member.family_name}
            </h3>
            <span className='body text-gray-500'>Self</span>
          </div>
        </div>
        <div className='flex flex-col gap-y-4'>
          <div className='grid grid-cols-2 xs:grid-cols-3 gap-4'>
            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Date of birth</span>
              </div>
              <div className='body sensitive'>
                {getFormattedDate(member.date_of_birth + 'T00:00:00')}
              </div>
            </div>

            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>SSN</span>
                {member.ssn && (
                  <IconButton
                    icon={showFullSSN ? EyeOffIcon : EyeIcon}
                    label={showFullSSN ? 'Hide SSN' : 'Show SSN'}
                    onClick={toggleSSNVisibility}
                  />
                )}
              </div>
              {member.ssn
                ? (
                  <div className='body font-mono sensitive'>
                    {renderFormattedSSN(member.ssn, showFullSSN)}
                  </div>
                  )
                : (
                  <div className='body'>—</div>
                  )}
            </div>

            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Gender</span>
              </div>
              <div className='body sensitive'>
                {capitalize(member.gender ?? '').replace('_', '-')}
              </div>
            </div>
            <div className={cn(occupation.length > 25 && 'col-span-2')}>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Occupation</span>
              </div>
              <div className='body'>{occupation}</div>
            </div>

            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Citizenship status</span>
              </div>
              <div className='body sensitive'>
                {member.is_us_citizen
                  ? 'American citizen'
                  : 'Not an American citizen'}
              </div>
            </div>
          </div>
        </div>
      </div>
      <RenewalsMemberInfoEditForm
        member={member}
        onEditOpen={handleEditOpen}
        onSave={handleEditSuccessful}
        onCancel={handleEditCancel}
        isMissingHouseholdIncome={isMissingHouseholdIncome}
      />
    </div>
  )
}

export default RenewalsMemberInfo
